// Interactive month/week calendar grid. Reused by the personal CalendarPage
// (all entries, class entries badged) and the class SchedulePanel (one clan).
// Tap an empty day to create; tap an entry chip to edit. App-blocking is still
// iOS-only — here a "block" is just shown on its days.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import type { CalendarEntry } from "@/api/calendar";
import { listClans } from "@/api/clans";
import { classTile } from "@/community/class-theme";
import { cx } from "@/components/ui";
import { KIND_ICON } from "./calendar-icons";
import {
  type Occurrence,
  addMonths,
  buildOccurrences,
  isToday,
  monthGridDays,
  startOfMonth,
  weekGridDays,
  ymdKey,
} from "./date-utils";

const MAX_CHIPS = 3;

export interface GridSeed {
  on_date?: string;
  due_at?: string;
}

export function CalendarGrid({
  entries,
  showClanBadges = false,
  canEdit,
  onCreate,
  onEdit,
}: {
  entries: CalendarEntry[];
  showClanBadges?: boolean;
  canEdit: boolean;
  onCreate: (seed: GridSeed) => void;
  onEdit: (entry: CalendarEntry) => void;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<"month" | "week">("month");
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Clan tint/emoji lookup (CalendarEntry only carries clan_id + clan_name).
  const clansQuery = useQuery({
    queryKey: ["clans"],
    queryFn: listClans,
    enabled: showClanBadges,
  });
  const clanTheme = useMemo(() => {
    const m = new Map<string, { color: string | null; emoji: string | null }>();
    for (const c of clansQuery.data ?? []) m.set(c.id, { color: c.color, emoji: c.icon_emoji });
    return m;
  }, [clansQuery.data]);

  const days = useMemo(
    () => (view === "month" ? monthGridDays(cursor) : weekGridDays(cursor)),
    [view, cursor],
  );
  const { byDay, unscheduled } = useMemo(() => buildOccurrences(entries, days), [entries, days]);

  const monthLabel = cursor.toLocaleDateString(i18n.language, { month: "long", year: "numeric" });
  const weekdayLabels = useMemo(() => {
    // Localized short weekday names, mon-first.
    const ref = new Date(2024, 0, 1); // a Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() + i);
      return d.toLocaleDateString(i18n.language, { weekday: "short" });
    });
  }, []);

  const step = (dir: number) =>
    setCursor((c) => (view === "month" ? addMonths(c, dir) : new Date(c.getFullYear(), c.getMonth(), c.getDate() + dir * 7)));

  const goToday = () => {
    const now = new Date();
    setCursor(view === "month" ? startOfMonth(now) : now);
  };

  const inCursorMonth = (d: Date) => view === "week" || d.getMonth() === cursor.getMonth();

  const chip = (occ: Occurrence, idx: number) => {
    const e = occ.entry;
    const theme = e.clan_id ? clanTheme.get(e.clan_id) : null;
    const isClass = e.source === "class";
    return (
      <button
        key={`${e.id}-${idx}`}
        onClick={(ev) => {
          ev.stopPropagation();
          onEdit(e);
        }}
        title={e.title}
        className={cx(
          "flex w-full items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[11px] font-medium",
          isClass ? classTile(theme?.color) : "bg-black/5 text-ink-soft",
        )}
      >
        <span aria-hidden>{showClanBadges && isClass && theme?.emoji ? theme.emoji : KIND_ICON[e.entry_kind]}</span>
        {e.entry_kind === "block" && e.start_time && (
          <span className="tabular-nums opacity-70">{e.start_time}</span>
        )}
        <span className="truncate">{e.title}</span>
      </button>
    );
  };

  const selected = selectedDay ? byDay.get(selectedDay) ?? [] : [];

  return (
    <div>
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button onClick={() => step(-1)} aria-label={t("Previous")} className="rounded-lg px-2 py-1 text-ink-soft hover:bg-black/5">‹</button>
          <span className="min-w-[8.5rem] text-center text-sm font-semibold capitalize">{monthLabel}</span>
          <button onClick={() => step(1)} aria-label={t("Next")} className="rounded-lg px-2 py-1 text-ink-soft hover:bg-black/5">›</button>
          <button onClick={goToday} className="ml-1 rounded-lg px-2 py-1 text-xs font-semibold text-brand-700 hover:bg-black/5">{t("Today")}</button>
        </div>
        <div className="inline-flex rounded-xl bg-black/5 p-1">
          {(["month", "week"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={cx(
                "rounded-lg px-2.5 py-1 text-xs font-semibold capitalize transition",
                view === v ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint",
              )}
            >
              {v === "month" ? t("Month") : t("Week")}
            </button>
          ))}
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
        {weekdayLabels.map((w, i) => (
          <div key={i} className="py-1">{w}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className={cx("grid grid-cols-7 gap-1", view === "month" ? "" : "mt-0")}>
        {days.map((d) => {
          const key = ymdKey(d);
          const occ = byDay.get(key) ?? [];
          const dim = !inCursorMonth(d);
          const today = isToday(d);
          return (
            <button
              key={key}
              onClick={() => (canEdit ? onCreate({ on_date: key }) : occ.length && setSelectedDay(key))}
              className={cx(
                "relative flex flex-col gap-0.5 rounded-xl border border-black/5 p-1 text-left align-top",
                view === "month" ? "min-h-[5.5rem]" : "min-h-[8rem]",
                dim ? "bg-transparent" : "bg-white",
                "hover:border-brand-300",
              )}
            >
              <span
                className={cx(
                  "inline-flex h-5 w-5 items-center justify-center self-end rounded-full text-[11px] font-semibold",
                  today ? "bg-brand-600 text-white" : dim ? "text-ink-faint/60" : "text-ink-soft",
                )}
              >
                {d.getDate()}
              </span>
              <div className="flex flex-col gap-0.5">
                {occ.slice(0, MAX_CHIPS).map((o, i) => chip(o, i))}
                {occ.length > MAX_CHIPS && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setSelectedDay(key);
                    }}
                    className="cursor-pointer rounded px-1 text-[10px] font-semibold text-brand-700 hover:underline"
                  >
                    {t("+{{count}} more", { count: occ.length - MAX_CHIPS })}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected-day detail */}
      {selectedDay && (
        <div className="mt-3 rounded-xl border border-black/10 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold">
              {new Date(`${selectedDay}T00:00:00`).toLocaleDateString(i18n.language, {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </span>
            <button onClick={() => setSelectedDay(null)} className="text-xs text-ink-faint hover:underline">{t("Close")}</button>
          </div>
          {selected.length === 0 ? (
            <p className="text-xs text-ink-faint">{t("Nothing on this day.")}</p>
          ) : (
            <div className="space-y-1">{selected.map((o, i) => chip(o, i))}</div>
          )}
          {canEdit && (
            <button
              onClick={() => onCreate({ on_date: selectedDay })}
              className="mt-2 text-xs font-semibold text-brand-700 hover:underline"
            >
              {t("Add entry")}
            </button>
          )}
        </div>
      )}

      {/* Unscheduled (homework/tasks with no due date) */}
      {unscheduled.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{t("Unscheduled")}</h3>
          <div className="mt-1 flex flex-wrap gap-1">
            {unscheduled.map((e, i) => chip({ entry: e, sortKey: "" }, i))}
          </div>
        </div>
      )}
    </div>
  );
}
